import { GetStaticPaths, NextPage } from 'next'
import { Case, Default, Switch } from 'react-if'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { projectTypes } from '@/constants/projectTypes'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { RegionContextProvider } from '@/contexts/RegionContext'
import { TheatricalRegionProvider } from '@/contexts/TheatricalRegionContext/TheatricalRegionContext'
import { Seo } from '@/layout/Seo'
import { MovieJsonLd, PodcastSeriesJsonLd, TVSeriesJsonLd } from '@/layout/Seo/JsonLd'
import {
  getJsonLdForMovie,
  getJsonLdForPodcastSeries,
  getJsonLdForTVSeries,
  useMetaForProject,
} from '@/layout/Seo/ProjectSeoHelper'
import { GetAppBannerProvider } from '@/organisms/GetAppBanner'
import { ShopifyConfigProvider } from '@/organisms/HorizontalShopifyMerchScroller/ShopifyConfigContext'
import { WatchDepartment } from '@/organisms/WatchDepartments/WatchDepartments'
import { getPageByPath } from '@/services/CmsService/Pages'
import { CatalogTitle, fetchTitle } from '@/services/ContentCatalog'
import { createWatchProjectDeepLink } from '@/services/DeepLinkService/DeepLinkService'
import { getFeatureAnnouncement } from '@/services/FeatureAnnouncementService'
import { isInGuildAccess } from '@/services/PhaseManagerService'
import { getContentfulProjectTheme } from '@/services/ProjectThemeService'
import {
  Project,
  ProjectMeta,
  fetchTheatricalReleaseData,
  fetchTheatricalReleaseMono,
  getProjectAbout,
  getWatchProjectEarlyAccess,
} from '@/services/ProjectsService'
import { PageModel, fetchPageDataContext } from '@/services/RenderService'
import { getShopifyConfigurationForProject } from '@/services/ShopifyConfigurationService/ShopifyConfigurationService'
import {
  GetTheatricalPreReleasePageDataResult,
  getTheatricalPreReleasePageData,
} from '@/services/TheatricalPreReleaseService'
import { ProjectTheme, TheatricalRegion } from '@/types/codegen-contentful'
import { TheatricalReleaseObject } from '@/types/codegen-federation'
import { getProjectInEarlyAccess } from '@/utils/GuildUtil'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { determinePageRevalidation } from '@/utils/nextUtils/revalidation'
import { omitUndefineds } from '@/utils/object'
import { loadTranslations } from '@/utils/translate/translate-server'
import { WatchProjectView, WatchProjectViewProps } from '@/views/WatchProjectView'

interface WatchProjectPageProps extends WatchProjectViewProps {
  catalogTitle?: CatalogTitle
  supportedTheatricalRegions: TheatricalRegion[]
  theatricalRegion: TheatricalRegion[]
  locale: string
  watchProjectPage: PageModel
  contentfulProjectTheme: ProjectTheme
  deepLink: string
  projectSlug: string
  pageData: GetTheatricalPreReleasePageDataResult
  theatricalRelease: TheatricalReleaseObject
  isProjectInEarlyAccess: boolean
  projectData: Project
}

export const WatchProjectPage: NextPage<WatchProjectPageProps & ProjectMeta> = (props) => {
  const meta = useMetaForProject(props)

  return (
    <>
      <Seo {...meta} nofollow={!props.public} noindex={!props.public} path={`${paths.watch.index}/${props.slug}`} />
      <Switch>
        <Case condition={props.projectType === projectTypes.movie}>
          <MovieJsonLd {...getJsonLdForMovie(props)} />
        </Case>
        <Case condition={props.projectType === projectTypes.podcast}>
          <PodcastSeriesJsonLd {...getJsonLdForPodcastSeries(props)} />
        </Case>
        <Default>
          <TVSeriesJsonLd {...getJsonLdForTVSeries(props)} />
        </Default>
      </Switch>
      <ShopifyConfigProvider
        storeUrl={props.shopifyConfig?.storeUrl}
        accessToken={props.shopifyConfig?.accessToken}
        handle={props.shopifyConfig?.handle}
        storeHref={props.shopifyConfig?.storeHref}
      >
        <ProjectMetaProvider project={props}>
          <TheatricalRegionProvider
            supportedTheatricalRegions={props.supportedTheatricalRegions}
            regionData={props.theatricalRegion}
          >
            <RegionContextProvider>
              <GetAppBannerProvider deepLink={props.deepLink} projectSlug={props.projectSlug} context="watch">
                <WatchProjectView {...props} />
              </GetAppBannerProvider>
            </RegionContextProvider>
          </TheatricalRegionProvider>
        </ProjectMetaProvider>
      </ShopifyConfigProvider>
    </>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

// TODO - Delete this file and move back to server side props.  This is a temporary fix to get Dry Bar Comedy loading faster with 202 episodes returned all at once.  The listWatchables should be quite functional in the near future which will add pagination to fetching episodes.
export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/watch/dry-bar-best-of.tsx' },
  async ({ federationClient, locale, next404, params, preview }) => {
    const projectSlug = 'dry-bar-best-of'

    const projectMeta = await getProjectAbout({ slug: projectSlug }, federationClient, { locale, preview })
    if (!projectMeta) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    const theatricalSlug = projectSlug
    const shouldIncludePreview = isDevelopmentEnvironment()
    const theatricalRelease = await fetchTheatricalReleaseData({
      projectType: projectMeta.projectType,
      primaryFlowPhases: projectMeta?.primaryFlowPhases || [],
      theatricalSlug,
      shouldIncludePreview,
      locale,
    })

    const regionData = theatricalRelease?.regionCollection ?? null
    const supportedTheatricalRegions = theatricalRelease?.supportedTheatricalRegions ?? null
    const hasPayItForward = projectMeta?.pifEnabled || false
    const shopifyConfig = await getShopifyConfigurationForProject(projectMeta.slug, { locale, preview })
    const theatricalReleaseMono = await fetchTheatricalReleaseMono({
      projectType: projectMeta.projectType,
      primaryFlowPhases: projectMeta?.primaryFlowPhases || [],
      theatricalSlug,
      client: federationClient,
    })

    const page = await getPageByPath(`${paths.watch.index}/${projectSlug}`, { preview, locale })
    const pageData = await getTheatricalPreReleasePageData({ slug: projectSlug }, federationClient, {
      locale,
      preview,
    })
    const pageDataContext = page ? await fetchPageDataContext(page, preview, locale) : null
    const catalogTitle = await fetchTitle<CatalogTitle>(locale, projectMeta?.contentCatalogId)
    const projectData = await getWatchProjectEarlyAccess(projectSlug, projectMeta, federationClient)

    const isProjectInEarlyAccess = Boolean(
      isInGuildAccess(projectData?.primaryFlowPhases ?? []) || getProjectInEarlyAccess(projectData),
    )

    const contentfulProjectTheme = await getContentfulProjectTheme(projectSlug, preview)
    const path = `${paths.watch.index}/${projectSlug}`

    const deepLink = await createWatchProjectDeepLink(
      {
        path,
        projectSlug,
        projectName: projectMeta?.name,
        contentName: projectMeta?.name,
        audioLocale: locale,
        captionLocale: locale,
      },
      {
        campaign: 'prompt-app-install',
        feature: 'app-prompt',
        channel: 'angel-web',
      },
    )

    return {
      props: {
        catalogTitle: omitUndefineds(catalogTitle),
        department: WatchDepartment.watch,
        hasPayItForward,
        locale,
        shopifyConfig,
        theatricalRegion: regionData,
        supportedTheatricalRegions,
        projectSlug,
        deepLink,
        ...projectMeta,
        featureAnnouncement: getFeatureAnnouncement(params?.project as string),
        page,
        pageData,
        pageDataContext,
        contentfulProjectTheme,
        theatricalRelease: theatricalReleaseMono,
        isProjectInEarlyAccess,
        projectData,
        ...(await loadTranslations(locale, [
          'common',
          'home',
          'watch',
          'app-promo',
          'ads',
          'account',
          'guild',
          'theatrical-presales',
        ])),
      },
      revalidate: determinePageRevalidation(page, pageDataContext),
    }
  },
)

export default WatchProjectPage
